import { useRouter } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipVariant } from 'cpool-components/general/Tooltip/types';
import { useTranslation } from 'react-i18next';
import { LuUsersRound } from 'react-icons/lu';

import { RepresentationItem } from './RepresentationItem';
import { HeaderButton } from '../../HeaderButton';
import { CountBadge } from '~/features/dashboard/components/CountBadge';
import { IconBadge } from '~/features/dashboard/components/IconBadge';
import { useRepresentedUsers } from '~/hooks/useRepresentedUser';
import useUserInfo from '~/hooks/useUserInfo';
import { sortRepresentedUsersByActiveAbsence } from '~/utils/representation';

export const Representation: React.FC = () => {
    const { t } = useTranslation();
    const user = useUserInfo();
    const router = useRouter();

    const { data } = useRepresentedUsers(user?.id);

    if (!data?.length) return null;

    const representationCount = data.reduce(
        (acc, item) => acc + (item.absence?.length ?? 0) + (item.delegation ? 1 : 0),
        0,
    );

    if (representationCount === 0) return null;

    const sortedData = sortRepresentedUsersByActiveAbsence(data);

    return (
        <Tooltip>
            <TooltipTrigger>
                <div className="flex items-center gap-2">
                    <HeaderButton
                        iconLeft={<LuUsersRound />}
                        onClick={() => {
                            router.navigate({ to: '/dashboard' });
                        }}
                    >
                        {t('DASHBOARD.REPRESENTATION.TITLE', { count: data?.length })}
                    </HeaderButton>
                </div>
            </TooltipTrigger>
            <TooltipContent variant={TooltipVariant.LIGHT} className="bg-white p-6">
                <div className="flex items-center justify-between gap-3 pb-2">
                    <div className="flex items-center gap-2">
                        <IconBadge icon={<LuUsersRound />} className="text-blue-text bg-blue-cloud" />
                        <span className="text-base font-medium text-grey whitespace-nowrap">
                            {t('DASHBOARD.REPRESENTATION.TITLE', { count: data.length })}
                        </span>
                    </div>
                    <CountBadge count={representationCount} className="text-blue-text bg-blue-cloud" />
                </div>
                {sortedData.map((item, index) => {
                    return <RepresentationItem key={index} data={item} />;
                })}
            </TooltipContent>
        </Tooltip>
    );
};
